import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Volume2, CheckCircle, XCircle, ArrowLeft, RefreshCcw } from 'lucide-react';

const FlashCardGame = ({ category, words, onFinish, onUpdateProgress, onBack }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [shaking, setShaking] = useState(false);

    // Filter words by category if needed, or use the passed list
    const gameWords = words;
    const currentWord = gameWords[currentIndex];

    useEffect(() => {
        // Reset state when category changes
        setCurrentIndex(0);
        setIsFlipped(false);
    }, [category]);

    const handleCorrect = () => {
        // Confetti effect
        confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#FFD1DC', '#CAF0F8', '#E2F0CB', '#FEF9E7'] // Pastel colors
        });

        // Mark as learned (logic to be connected to tracker)
        onUpdateProgress(currentWord.id, true);

        nextCard();
    };

    const handleNeedsPractice = () => {
        // Shake effect
        setShaking(true);
        setTimeout(() => setShaking(false), 500);

        // Mark for review
        onUpdateProgress(currentWord.id, false);

        // Delay next card slightly to let the feedback sink in
        setTimeout(() => {
            nextCard();
        }, 800)

    };

    const nextCard = () => {
        setIsFlipped(false);
        if (currentIndex < gameWords.length - 1) {
            setTimeout(() => setCurrentIndex(prev => prev + 1), 300);
        } else {
            onFinish();
        }
    };

    const playAudio = (wordItem) => {
        // Play name
        const utterance = new SpeechSynthesisUtterance(wordItem.word);
        utterance.lang = 'es-ES';
        utterance.rate = 0.9;
        window.speechSynthesis.speak(utterance);
    };

    const playSoundEffect = (wordItem) => {
        if (wordItem.audioPath) {
            const audio = new Audio(wordItem.audioPath);
            audio.play().catch(e => {
                console.log('Audio not found or blocked, falling back to speech', e);
                speakText(wordItem.sound);
            });
        } else if (wordItem.sound) {
            // Fallback: Just say the sound text ("¡Miau!")
            speakText(wordItem.sound);
        }
    };

    const speakText = (text) => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'es-ES';
        // Make sounds slightly higher pitch/faster for fun animal voices
        utterance.pitch = 1.2;
        utterance.rate = 1.1;
        window.speechSynthesis.speak(utterance);
    };

    // Progress Bar calculation
    const progress = ((currentIndex) / gameWords.length) * 100;

    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-4 w-full max-w-md mx-auto relative">

            {/* Top Bar */}
            <div className="w-full flex justify-between items-center mb-6">
                <button
                    onClick={onBack}
                    className="p-2 rounded-full bg-white text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors shadow-sm"
                >
                    <ArrowLeft size={24} />
                </button>
                <div className="w-full max-w-xs h-3 bg-gray-100 rounded-full mx-4 overflow-hidden shadow-inner">
                    <motion.div
                        className="h-full bg-gradient-to-r from-[#E2F0CB] to-[#CAF0F8] rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.5 }}
                    />
                </div>
                <span className="text-sm font-bold text-slate-400 font-nunito">{currentIndex + 1}/{gameWords.length}</span>
            </div>


            {/* Main Card - Polaroid Style */}
            <AnimatePresence mode='wait'>
                <motion.div
                    key={currentIndex}
                    initial={{ x: 300, opacity: 0, rotate: 5 }}
                    animate={{
                        x: shaking ? [0, -10, 10, -10, 10, 0] : 0,
                        opacity: 1,
                        rotate: Math.random() * 4 - 2 // Subtle random rotation for natural feel
                    }}
                    exit={{ x: -300, opacity: 0, rotate: -5 }}
                    transition={{ type: "spring", stiffness: 200, damping: 20 }}
                    className="w-full aspect-[3/4] max-h-[500px] bg-white rounded-2xl shadow-xl transition-shadow flex flex-col items-center p-4 pb-8 relative"
                    style={{ transformStyle: 'preserve-3d' }}
                >
                    {/* Tape Effect (Decoration) */}
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-32 h-8 bg-sky-100/50 rotate-1 rounded-sm backdrop-blur-sm z-10 border border-white/40"></div>

                    {/* Image Area - Square Photo Look */}
                    <motion.div
                        className="w-full aspect-square bg-slate-50 rounded-xl overflow-hidden mb-6 relative border-4 border-slate-100 shadow-inner group cursor-pointer"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => (currentWord.audioPath || currentWord.sound) ? playSoundEffect(currentWord) : playAudio(currentWord)}
                    >
                        {/* Fallback Icon */}
                        <div className="absolute inset-0 flex items-center justify-center bg-[#FEF9E7]/50 z-0">
                            {currentWord.Icon}
                        </div>

                        {/* Image */}
                        <img
                            src={currentWord.imagePath}
                            alt={currentWord.word}
                            className="w-full h-full object-cover z-10 relative transition-transform duration-700 group-hover:scale-110"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />

                        {/* Play Overlay Hint */}
                        <div className="absolute inset-0 z-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10">
                            <Volume2 size={48} className="text-white drop-shadow-lg" />
                        </div>
                    </motion.div>

                    {/* Word & Controls */}
                    <div className="text-center w-full mt-auto">
                        <motion.h2
                            className="text-5xl font-black text-slate-700 font-nunito mb-2 tracking-tight"
                            layoutId={`word-${currentWord.id}`}
                        >
                            {currentWord.word}
                        </motion.h2>

                        {currentWord.sound && (
                            <motion.p
                                className="text-xl font-bold text-[#0073E4] font-nunito mb-4 italic"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                            >
                                "{currentWord.sound}"
                            </motion.p>
                        )}

                        <div className="flex justify-center gap-4 flex-wrap">
                            <button
                                onClick={() => playAudio(currentWord)}
                                className="inline-flex items-center text-sm font-bold text-[#8B5E66] bg-[#FFD1DC]/30 px-4 py-2 rounded-full hover:bg-[#FFD1DC]/50 transition-colors"
                            >
                                <Volume2 size={18} className="mr-2" /> Escuchar
                            </button>
                            {(currentWord.audioPath || currentWord.sound) && (
                                <button
                                    onClick={() => playSoundEffect(currentWord)}
                                    className="inline-flex items-center text-sm font-bold text-[#0073E4] bg-[#CAF0F8]/50 px-4 py-2 rounded-full hover:bg-[#CAF0F8]/80 transition-colors"
                                >
                                    <Volume2 size={18} className="mr-2" /> Sonido
                                </button>
                            )}
                        </div>
                    </div>

                </motion.div>
            </AnimatePresence>

            {/* Controls */}
            <div className="flex justify-center gap-12 mt-10 w-full px-8">
                <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={handleNeedsPractice}
                    className="flex flex-col items-center gap-2 group"
                >
                    <div className="w-16 h-16 rounded-full bg-white border-2 border-[#FFD1DC] flex items-center justify-center shadow-md group-hover:bg-[#FFF0F3] group-hover:border-[#FFB7C5] transition-all">
                        <XCircle size={32} className="text-[#FFB7C5] group-hover:scale-110 transition-transform" />
                    </div>
                </motion.button>

                <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={handleCorrect}
                    className="flex flex-col items-center gap-2 group"
                >
                    <div className="w-20 h-20 rounded-full bg-white border-4 border-[#E2F0CB] flex items-center justify-center shadow-xl group-hover:bg-[#F1FBE9] group-hover:border-[#98C17F] transition-all -mt-3">
                        <CheckCircle size={44} className="text-[#98C17F] group-hover:scale-110 transition-transform" />
                    </div>
                </motion.button>
            </div>

        </div>
    );
};

export default FlashCardGame;
